import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// A response belongs to one account, customer and language. Never render the
// previous customer's data while the next one loads, even when requests race.
export default function useCockpitOverview({ lang, clientId, userId, accountClientId }) {
  const scopeKey = JSON.stringify([userId, clientId || accountClientId || null, lang]);
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  const requestId = useRef(0);
  const mounted = useRef(false);
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; requestId.current += 1; };
  }, []);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    const isCurrent = () => mounted.current && currentScope.current === scopeKey && requestId.current === id;
    try {
      const { data } = await api.get(`/cockpit/overview?lang=${encodeURIComponent(lang)}${clientId ? `&client_id=${encodeURIComponent(clientId)}` : ""}`);
      const expectedClient = clientId || accountClientId;
      if (!data?.client?.id || (expectedClient && data.client.id !== expectedClient)) {
        throw new Error("Cockpit response does not match requested customer");
      }
      if (isCurrent()) { setResult({ scopeKey, data }); setFailure(null); }
    } catch {
      if (isCurrent()) setFailure(scopeKey);
    }
  }, [scopeKey, lang, clientId, accountClientId]);
  useEffect(() => { load(); }, [load]);
  return { data: result?.scopeKey === scopeKey ? result.data : null, loadError: failure === scopeKey, load };
}
